/* Install on an unpublished Shopify theme first. No experiment runs by default. */
(() => {
  const BASE = 'https://crm.stepupshoes.co.il';
  const TOKEN_KEY = 'stepup_ab_assignment_v1';
  const VISITOR_KEY = 'stepup_ab_visitor_v1';
  if (window.__stepupAB) return;
  window.__stepupAB = true;
  const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const post = async (route, data) => {
    const r = await fetch(BASE + route, { method:'POST',headers:{'Content-Type':'text/plain;charset=UTF-8'},body:JSON.stringify(data),keepalive:true });
    if (!r.ok) throw new Error('AB request failed');
    return r.json();
  };
  const patches = changes => {
    const ops = changes.map(c => {
      const nodes = document.querySelectorAll(c.selector);
      if (nodes.length !== 1) throw new Error('AB selector must match one element');
      const node = nodes[0];
      if (c.type === 'text' && node.children.length) throw new Error('Text target must be a leaf element');
      if (c.type === 'image' && node.tagName !== 'IMG') throw new Error('Image target must be IMG');
      if (c.type === 'open' && node.tagName !== 'DETAILS') throw new Error('Open target must be DETAILS');
      const before = c.type === 'move' ? document.querySelector(c.value) : null;
      if (c.type === 'move' && (!before || before === node || node.contains(before))) throw new Error('Invalid move target');
      return {c,node,before};
    });
    const restore = [];
    try {
      for (const {c,node,before} of ops) {
        const old = { text:node.textContent, src:node.getAttribute('src'), srcset:node.getAttribute('srcset'), color:node.style.getPropertyValue('color'), background:node.style.getPropertyValue('background-color'), display:node.style.getPropertyValue('display'), opened:node.open, colorPriority:node.style.getPropertyPriority('color'), backgroundPriority:node.style.getPropertyPriority('background-color'), displayPriority:node.style.getPropertyPriority('display'), next:node.nextSibling, parent:node.parentNode };
        restore.push(() => {
          if (c.type === 'text') node.textContent = old.text;
          if (c.type === 'image') { if (old.src === null) node.removeAttribute('src'); else node.setAttribute('src',old.src); if (old.srcset === null) node.removeAttribute('srcset'); else node.setAttribute('srcset',old.srcset); }
          if (c.type === 'color') old.color ? node.style.setProperty('color',old.color,old.colorPriority) : node.style.removeProperty('color');
          if (c.type === 'background') old.background ? node.style.setProperty('background-color',old.background,old.backgroundPriority) : node.style.removeProperty('background-color');
          if (c.type === 'hide') old.display ? node.style.setProperty('display',old.display,old.displayPriority) : node.style.removeProperty('display');
          if (c.type === 'open') node.open = old.opened;
          if (c.type === 'move' && old.parent) old.parent.insertBefore(node,old.next?.parentNode === old.parent ? old.next : null);
        });
        if (c.type === 'text') node.textContent = c.value;
        if (c.type === 'image') { node.removeAttribute('srcset'); node.src = c.value; }
        if (c.type === 'color') node.style.setProperty('color',c.value,'important');
        if (c.type === 'background') node.style.setProperty('background-color',c.value,'important');
        if (c.type === 'hide') node.style.setProperty('display','none','important');
        if (c.type === 'open') node.open = true;
        if (c.type === 'move') before.before(node);
      }
    } catch (error) { for (const undo of restore.reverse()) { try { undo(); } catch {} } throw error; }
    return () => { for (const undo of restore.reverse()) { try { undo(); } catch {} } };
  };
  let busy = false;
  let retryTimer = null;
  let retryAt = null;
  let consentWaitTimer = null;
  let consentWaitAttempts = 0;
  let consentLoadRequested = false;
  let readyStarted = false;
  function scheduleRetry(value) {
    const at = Date.parse(value);
    if (!Number.isFinite(at) || at <= Date.now() || at === retryAt) return;
    if (retryTimer) clearTimeout(retryTimer);
    retryAt = at;
    retryTimer = setTimeout(() => { retryTimer = null; retryAt = null; ready(); }, Math.min(at - Date.now() + 250, 2147483647));
  }
  function startIfAllowed() {
    if (!allowed()) { readyStarted = false; return; }
    if (readyStarted) return;
    readyStarted = true;
    ready();
  }
  function stopConsentWait() {
    if (consentWaitTimer) clearTimeout(consentWaitTimer);
    consentWaitTimer = null;
    consentWaitAttempts = 0;
  }
  function initConsentApi() {
    if (window.Shopify?.customerPrivacy) {
      stopConsentWait();
      startIfAllowed();
      return true;
    }
    const loadFeatures = window.Shopify?.loadFeatures;
    if (typeof loadFeatures === 'function' && !consentLoadRequested) {
      consentLoadRequested = true;
      try {
        loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => {
          if (!error && window.Shopify?.customerPrivacy) {
            stopConsentWait();
            startIfAllowed();
          } else scheduleConsentCheck();
        });
      } catch { scheduleConsentCheck(); }
    }
    return false;
  }
  function scheduleConsentCheck() {
    if (consentWaitTimer || consentWaitAttempts >= 120) return;
    consentWaitTimer = setTimeout(() => {
      consentWaitTimer = null;
      consentWaitAttempts++;
      if (!initConsentApi()) scheduleConsentCheck();
    },250);
  }
  async function run() {
    if (busy || !allowed() || !(/^\/$|^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:products|collections)\/[^/]+$/.test(location.pathname))) return;
    busy = true;
    let assigned = [];
    let undoVariants = [];
    try {
      let visitor = localStorage.getItem(VISITOR_KEY);
      if (!visitor) { visitor = crypto.randomUUID(); localStorage.setItem(VISITOR_KEY,visitor); }
      // No per-tab fallback: a visitor without persistent storage remains in A and outside the experiment.
      if (localStorage.getItem(VISITOR_KEY) !== visitor) return;
      // Resolve every page view against the server, so paused and ended tests return to A.
      // The visitor UUID remains stable and the server keeps the original allocation.
      const openableDescription = document.querySelectorAll('details[id^="Details-RealDesc-"]').length === 1;
      const waSizeHelp = document.querySelectorAll('.su-wa-after-buttons > a.su-wa-size-help').length === 1;
      const data = await post('/api/ab/assign',{visitor,path:location.pathname,openable_description:openableDescription,wa_size_help:waSizeHelp});
      const assignments = data.assignments || (data.assignment ? [{...data.assignment,changes:data.changes}] : []);
      if (!assignments.length) { localStorage.removeItem(TOKEN_KEY); scheduleRetry(data.retry_at); return; }
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = null; retryAt = null;
      assigned = assignments;
      window.__stepupABChanges = window.__stepupABChanges || {};
      for (const a of assignments) window.__stepupABChanges[a.experiment] = a.changes;
      const waExperiment=assignments.some(a=>a.changes?.some(c=>c.type==='hide'&&c.selector==='.su-wa-after-buttons'));
      const waBlock=waExperiment ? document.querySelector('.su-wa-after-buttons') : null;
      if (waBlock) { waBlock.dataset.stepupAbWa='true'; waBlock.hidden=false; }
      // Shopify order attributes survive ordinary cart checkout. Express checkout coverage is checked in the report.
      const root = window.Shopify?.routes?.root || '/';
      const attributes = Object.fromEntries(assignments.map(a => [`__stepup_ab_${a.experiment}`,a.token]));
      attributes.__stepup_ab = assignments[0].token;
      const saved = await fetch(root + 'cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({attributes})});
      if (!saved.ok) throw new Error('cart attribution unavailable');
      for (const a of assignments) if (a.variant === 'B') {
        const undo = patches(a.changes);
        undoVariants.push(undo);
        window.__stepupABUndo = window.__stepupABUndo || {};
        window.__stepupABUndo[a.experiment] = undo;
      }
      localStorage.setItem(TOKEN_KEY,JSON.stringify(assignments));
      window.stepupABAssignments = assignments;
      window.stepupABAssignment = assignments[0];
      for (const a of assignments) await post('/api/ab/event',{token:a.token,event:'view_item',event_id:crypto.randomUUID(),path:location.pathname});
      // Enrich the existing CRO sender, without sending a second CRO event.
      const cro = window.stepupCroFetch;
      if (cro) window.stepupCroFetch = (url, init = {}) => {
        if (allowed() && String(url).includes('/api/cro/events') && typeof init.body === 'string') {
          try { const body = JSON.parse(init.body); body.metadata = {...body.metadata,ab_assignments:assignments.map(a=>({experiment:a.experiment,variant:a.variant})),ab_visitor:visitor}; init={...init,body:JSON.stringify(body)}; } catch {}
        }
        return cro(url,init);
      };
    } catch {
      console.warn('StepUp AB: experiment unavailable; verify preview before launch');
      for (const undo of undoVariants.reverse()) undo();
      const waBlock=document.querySelector?.('.su-wa-after-buttons');
      if (waBlock?.dataset.stepupAbWa==='true') delete waBlock.dataset.stepupAbWa;
      try { localStorage.removeItem(TOKEN_KEY); delete window.stepupABAssignment; delete window.stepupABAssignments; } catch {}
      if (assigned.length) {
        const attributes = Object.fromEntries(assigned.map(a => [`__stepup_ab_${a.experiment}`,'']));
        attributes.__stepup_ab='';
        try { await fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({attributes})}); } catch {}
        if (allowed()) for (const a of assigned) { try { await post('/api/ab/event',{token:a.token,event:'variant_error',event_id:crypto.randomUUID()}); } catch {} }
      }
    }
  }
  function ready() {
    const start=()=>navigator.locks?.request ? navigator.locks.request('stepup-ab-init',run).catch(()=>{}) : run();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true}); else void start();
  }
  document.addEventListener('visitorConsentCollected', () => {
    if (window.Shopify?.customerPrivacy) startIfAllowed();
    else { readyStarted = false; initConsentApi(); scheduleConsentCheck(); }
    if (!allowed()) { readyStarted = false; try { localStorage.removeItem(TOKEN_KEY); } catch {} }
  });
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', () => { if (!initConsentApi()) scheduleConsentCheck(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && allowed() && retryAt && Date.now() >= retryAt) ready(); });
  if (!initConsentApi()) scheduleConsentCheck();
})();
